import { forwardRef } from 'react';
import { Image } from '../design-system';
import { useIsSleek } from '../theme';
import { TiltCard } from './TiltCard';
import {
  CLEAR_TRANSITION_MS,
  DIM_FADE_MS,
  REVEAL_FADE_MS,
  type AnimMode,
} from './readyStep.constants';

export interface SlotTileProps {
  packImageUrl: string | null;
  title: string;
  /** Pre-computed brightness for this tile (1.0 for centre, dimmer
   *  for neighbours; 1.0 for every tile during spin). */
  brightness: number;
  /** `true` for the visually-centred tile in idle mode. Wraps its
   *  pack image in `<TiltCard>` for the subtle hover effect. */
  isCenter: boolean;
  /** `true` for the lane tile that lands in the viewport centre
   *  after the spin's `K` steps. Stays opaque in `cleared` while the
   *  neighbours fade. */
  isLanded: boolean;
  /** Drives the opacity-fade behaviour:
   *  - `cleared` → all but landed go transparent (neighbour fade).
   *  - `reveal`  → every tile goes transparent (cross-fade to glow).
   *  - else      → 1.0. */
  animMode: AnimMode;
  /** `true` only while the lane is fully at rest (idle mode AND no
   *  manual drag glide in flight). Gates the hover-tilt and the
   *  tap-to-open cursor. */
  isResting: boolean;
  /** Tap-to-open — set on the centre tile only. The parent guards
   *  against the click that ends a drag gesture. */
  onSelect?: () => void;
  /** Transparent but still laid out — sleek keeps the neighbours out of
   *  sight until the spin starts, and the lane still measures its step. */
  hidden?: boolean;
}

/**
 * One pack tile on the slot lane. Visually identical to every other
 * tile in the lane (same image) — the distinguishing state per tile
 * comes from `brightness` (resting dim hierarchy) and `opacity`
 * (post-spin fades). The hover-tilt + glare interaction lives only
 * on the centre tile (idle anchor); the landed tile uses its own
 * fade out into the `<RevealGlow>` overlay rather than a tilt.
 *
 * `ref` is forwarded so the parent can read `getBoundingClientRect`
 * on the centre tile to measure tile-step at runtime, which keeps
 * the slot animation honest against Tailwind container-query
 * breakpoints without duplicating the size table in JS.
 */
export const SlotTile = forwardRef<HTMLDivElement, SlotTileProps>(function SlotTile(
  { packImageUrl, title, brightness, isCenter, isLanded, animMode, isResting, onSelect, hidden = false },
  ref,
) {
  // Sleek (partner mockup) shows a larger pack on phones: 287px ≈ the
  // mockup's 250×370 pouch on a 402px screen, scaled to 390. Every width is
  // then multiplied by the operator's pack size (`--reveal-pack-scale`, 1 =
  // as is) and still capped by the stage height.
  const isSleek = useIsSleek();
  // cleared: hide all but landed
  // reveal:  hide every tile (landed crossfades to the glow overlay)
  const opacity =
    hidden || (animMode === 'cleared' && !isLanded)
      ? 0
      : animMode === 'reveal'
        ? 0
        : 1;

  const packImage = (
    <Image
      src={packImageUrl}
      alt={`${title} pack`}
      className="absolute inset-0"
      imgClassName="absolute inset-0 h-full w-full object-contain"
      width={421}
      height={526}
    />
  );

  return (
    <div
      ref={ref}
      onClick={onSelect}
      className={`
        relative flex-shrink-0
        ${isSleek ? 'w-[min(calc(287px*var(--reveal-pack-scale,1)),var(--reveal-pack-max-w))]' : 'w-[min(calc(260px*var(--reveal-pack-scale,1)),var(--reveal-pack-max-w))]'}
        @[500px]:w-[min(calc(280px*var(--reveal-pack-scale,1)),var(--reveal-pack-max-w))]
        @[800px]:w-[min(calc(360px*var(--reveal-pack-scale,1)),var(--reveal-pack-max-w))]
        @[1100px]:w-[min(calc(420px*var(--reveal-pack-scale,1)),var(--reveal-pack-max-w))]
        ${onSelect && isResting ? 'cursor-pointer' : ''}
      `}
      style={{
        aspectRatio: '421 / 526',
        filter: `brightness(${brightness})`,
        opacity,
        // Two opacity transitions sharing the same channel:
        //   • `cleared` — neighbours fade out (500ms).
        //   • `reveal`  — landed pack fades out while the glow overlay
        //     fades in (500ms crossfade).
        // Filter fades the dim/undim swing when a manual drag starts /
        // the lane settles, so the brightness hierarchy doesn't pop.
        transition: `opacity ${animMode === 'reveal' ? REVEAL_FADE_MS : CLEAR_TRANSITION_MS}ms ease-out, filter ${DIM_FADE_MS}ms ease-out`,
      }}
    >
      {isCenter ? (
        <TiltCard enabled={isResting} glareMaskUrl={packImageUrl}>
          {packImage}
        </TiltCard>
      ) : (
        packImage
      )}
    </div>
  );
});
